import type { ClipAnimation } from '../timeline/types';
import type { AttachToConfig } from '../layout';
import type { AudioLayer, Background, Layer, LayerId, PieceModel, TimeMs, Transform2D } from './types';
import { updateLayerById } from './nested-layers';

export type PieceAction =
  | { type: 'syncFromCanvas'; model: PieceModel }
  | { type: 'addLayer'; layer: Layer; index?: number }
  | { type: 'removeLayer'; id: LayerId }
  | { type: 'addAudioLayer'; layer: AudioLayer }
  | { type: 'removeAudioLayer'; id: LayerId }
  | { type: 'setAudioLayers'; layers: AudioLayer[] }
  | { type: 'setLayerTransform'; id: LayerId; transform: Transform2D }
  | { type: 'patchLayerContent'; id: LayerId; patch: Record<string, unknown> }
  | {
      type: 'patchAudioContent';
      id: LayerId;
      patch: { gain?: number; fadeOutMs?: number; trim?: { in: TimeMs; out: TimeMs } };
    }
  | { type: 'setLayerEnter'; id: LayerId; enter: ClipAnimation | null }
  | { type: 'setLayerTiming'; id: LayerId; start?: TimeMs; duration?: TimeMs; fit?: 'fixed' | 'fill' }
  | { type: 'setLayerVisible'; id: LayerId; visible: boolean }
  | { type: 'setLayerLock'; id: LayerId; locked: boolean }
  | { type: 'setLayerAttach'; id: LayerId; attachTo?: AttachToConfig }
  | { type: 'setBackground'; background: Background }
  | { type: 'replaceModel'; model: PieceModel };

// Farthest end of the piece: fixed clips, entrance animations, and audio.
const anchorDuration = (layers: Record<LayerId, Layer>): TimeMs => {
  let max = 0;
  for (const layer of Object.values(layers)) {
    if (layer.kind === 'audio') {
      max = Math.max(max, layer.start + layer.duration);
      continue;
    }
    if (layer.fit === 'fixed') max = Math.max(max, layer.start + layer.duration);
    if (layer.enter) max = Math.max(max, layer.start + layer.enter.duration);
  }
  return max;
};

// Fill layers span the whole piece, so they stretch to the total duration.
const refit = (model: PieceModel): PieceModel => {
  const duration = anchorDuration(model.layers);
  const layers: Record<LayerId, Layer> = {};
  let changed = false;

  for (const id of Object.keys(model.layers)) {
    const layer = model.layers[id];
    if (!layer) continue;
    if (layer.kind !== 'audio' && layer.fit === 'fill') {
      const target = Math.max(0, duration - layer.start);
      if (layer.duration !== target) {
        layers[id] = { ...layer, duration: target };
        changed = true;
        continue;
      }
    }
    layers[id] = layer;
  }

  if (!changed && duration === model.duration) return model;
  return { ...model, duration, layers };
};

export const pieceReducer = (state: PieceModel, action: PieceAction): PieceModel => {
  switch (action.type) {
    // Restore a whole model snapshot (undo/redo, or opening a saved piece).
    // The renderer reconciles the canvas; refit so a host can't ship a duration that
    // ignores an audio-only tail (the piece would truncate on playback and export).
    case 'replaceModel':
      return refit(action.model);

    case 'syncFromCanvas': {
      // Take everything from the canvas and add back the audio, which isn't part of it.
      const audio: Record<LayerId, Layer> = {};
      for (const id of Object.keys(state.layers)) {
        const layer = state.layers[id];
        if (layer && layer.kind === 'audio') audio[id] = layer;
      }
      return refit({ ...action.model, layers: { ...action.model.layers, ...audio } });
    }

    case 'addLayer': {
      if (state.layers[action.layer.id]) return state;
      const layers = { ...state.layers, [action.layer.id]: action.layer };
      // Audio isn't part of the visual stack; everything else is.
      let layerOrder = state.layerOrder;
      if (action.layer.kind !== 'audio') {
        layerOrder =
          action.index === undefined
            ? [...state.layerOrder, action.layer.id]
            : [...state.layerOrder.slice(0, action.index), action.layer.id, ...state.layerOrder.slice(action.index)];
      }
      return refit({ ...state, layers, layerOrder });
    }

    case 'removeLayer': {
      if (!state.layers[action.id]) return state;
      const layers = { ...state.layers };
      delete layers[action.id];
      return refit({ ...state, layers, layerOrder: state.layerOrder.filter(id => id !== action.id) });
    }

    case 'addAudioLayer':
      return refit({ ...state, layers: { ...state.layers, [action.layer.id]: action.layer } });

    case 'removeAudioLayer': {
      if (!state.layers[action.id]) return state;
      const layers = { ...state.layers };
      delete layers[action.id];
      return refit({ ...state, layers });
    }

    case 'setAudioLayers': {
      const layers: Record<LayerId, Layer> = {};
      for (const id of Object.keys(state.layers)) {
        const layer = state.layers[id];
        if (layer && layer.kind !== 'audio') layers[id] = layer;
      }
      for (const audio of action.layers) layers[audio.id] = audio;
      return refit({ ...state, layers });
    }

    case 'setLayerTransform': {
      const layer = state.layers[action.id];
      if (!layer || layer.kind === 'audio') return state; // audio has no transform
      return { ...state, layers: { ...state.layers, [action.id]: { ...layer, ...action.transform } } };
    }

    case 'patchLayerContent':
      return updateLayerById(state, action.id, layer =>
        layer.kind === 'audio' ? layer : ({ ...layer, content: { ...layer.content, ...action.patch } } as Layer)
      );

    case 'patchAudioContent':
      return updateLayerById(state, action.id, layer =>
        layer.kind === 'audio' ? ({ ...layer, content: { ...layer.content, ...action.patch } } as Layer) : layer
      );

    case 'setLayerEnter': {
      const layer = state.layers[action.id];
      if (!layer || layer.kind === 'audio') return state;
      const next = { ...layer };
      if (action.enter) next.enter = action.enter;
      else delete next.enter;
      return refit({ ...state, layers: { ...state.layers, [action.id]: next } });
    }

    case 'setLayerTiming': {
      const layer = state.layers[action.id];
      if (!layer) return state;
      const next = { ...layer };
      if (action.start !== undefined) next.start = Math.max(0, action.start);
      if (action.duration !== undefined) next.duration = action.duration;
      if (action.fit !== undefined) next.fit = action.fit;
      return refit({ ...state, layers: { ...state.layers, [action.id]: next } });
    }

    case 'setLayerVisible': {
      const layer = state.layers[action.id];
      if (!layer || layer.visible === action.visible) return state;
      return { ...state, layers: { ...state.layers, [action.id]: { ...layer, visible: action.visible } } };
    }

    case 'setLayerLock': {
      const layer = state.layers[action.id];
      if (!layer || layer.locked === action.locked) return state;
      return { ...state, layers: { ...state.layers, [action.id]: { ...layer, locked: action.locked } } };
    }

    case 'setLayerAttach':
      return updateLayerById(state, action.id, layer => {
        if (layer.kind === 'audio') return layer;
        const next = { ...layer };
        if (action.attachTo) next.attachTo = action.attachTo;
        else delete next.attachTo;
        return next;
      });

    case 'setBackground':
      return { ...state, background: action.background };
  }
};
